import React, { MouseEventHandler, PropsWithChildren } from 'react';
import extendClassNameWithModifier from '../../../lib/bemUtils/bem';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { isNullOrUndefined } from '../../../lib/typeGuards';

export type SkipLinkModifier = 'bottom';

type SkipLinkProps = {
  id: string;
  modifier?: Modifier<SkipLinkModifier>;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  focusTarget?: boolean;
};
/**
 * It's a link that allows to skip content for keyboard users.
 */
export function SkipLink({ id, children, onClick, modifier, focusTarget = false }: PropsWithChildren<SkipLinkProps>) {
  /**
   * We decided to :focus the SkipLinkTarget manually instead of a :target because in modal dialogs
   * the focusTrap loses focus on :target (focus is on body then) and doesn't set focus
   * to the nearest focusable element after pressing tab again.
   */
  const setFocusToTarget = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault();
    const element = document.getElementById(id);
    if (isNullOrUndefined(element)) {
      return;
    }
    const onBlur = () => {
      element.style.visibility = 'hidden';
      element.removeAttribute('tabindex');
    };

    element.addEventListener('blur', onBlur, { once: true });
    element.setAttribute('tabindex', '-1');
    element.style.visibility = 'visible'; // keep this, otherwise no focus possible!
    element.focus();
  };
  return (
    <a
      href={`#${id}`}
      className={extendClassNameWithModifier('skip-link', modifier)}
      onClick={focusTarget ? setFocusToTarget : onClick}
    >
      {children}
    </a>
  );
}
